import React, { useState } from "react";
import { useNavigate, Link, useParams, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link as MuiLink,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import authService from "../../services/auth.service";
import "../../styles/components.css";
import { localStorageInstance } from "../../utils/storage";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  const validateForm = () => {
    const errors = {};

    if (!formData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement password reset logic here
      await authService.resetPassword(params.get("token"), formData.newPassword);
      navigate("/login");
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page-container">
      <Grid container height={"100vh"}>
        <Grid item xs={12} sm={12} md={5}>
          <div className="login-page-image-container" />
        </Grid>

        <Grid item xs={12} sm={12} md={7} className="login-page-right-panel">
          <Container className="right-panel-auth">
            <Box
              className="auth-box"
              sx={{
                maxWidth: isMobile ? "100%" : "85%",
              }}
            >
              <Typography className="auth-heading">
                Reset your password
              </Typography>

              <Typography sx={{ my: 3, fontSize: 32, fontWeight: 700 }}>
                Reset Password
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={handleSubmit}
                className="auth-form-box"
              >
                <Box mb={4.5}>
                  <Typography sx={{ fontSize: 24, fontWeight: 500 }}>
                    New Password
                  </Typography>
                  <TextField
                    fullWidth
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    hiddenLabel
                    error={!!validationErrors.newPassword}
                    helperText={validationErrors.newPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPassword}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "20px 16px",
                        fontSize: "20px",
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 500 }}>
                    Confirm Password
                  </Typography>
                  <TextField
                    fullWidth
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    hiddenLabel
                    error={!!validationErrors.confirmPassword}
                    helperText={validationErrors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "20px 16px",
                        fontSize: "20px",
                      },
                    }}
                  />
                </Box>

                <Button
                  sx={{
                    mt: 3,
                  }}
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  className="button-primary"
                  disabled={loading}
                >
                  Reset Password
                </Button>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: 16, fontWeight: 400 }}
                  >
                    Remember your password?{" "}
                    <MuiLink
                      component={Link}
                      to="/login"
                      className="signup-link"
                    >
                      Sign In
                    </MuiLink>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
